import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { CommandClickEventArgs, CommandModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { SfCommandButtonType } from 'src/app/base/models/syncfusion-data';
import { ColorPickerFieldComponent } from 'src/app/base/shared/color-picker-field/color-picker-field.component';
import { CollapseButtonComponent } from 'src/app/base/shared/collapse-button/collapse-button.component';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { TenantThemeOverrideMode } from 'src/app/base/services/theme.model';
import { ThemeService } from 'src/app/base/services/theme.service';
import { tenantThemeOverrideModes, tenantThemeOverrideVariables } from 'src/app/base/services/tenant-theme-overrides.constants';
import { ShowcaseOption } from '../../utils/theme-showcase-data';
import { ThemeTokenOverrideRow, buildOverridesFromRows, sortThemeTokenOverrideRows } from '../../utils/theme-token-override.util';
import {
  resolveThemeTokenOverrideAction,
  resolveThemeTokenOverrideValueForSelection
} from '../../utils/theme-token-override-rules.util';

interface ThemeTokenOverrideEditorForm {
  overrideVariable: FormControl<string | null>;
  overrideMode: FormControl<TenantThemeOverrideMode | null>;
  overrideValue: FormControl<string | null>;
}

@Component({
  selector: 'app-theme-token-override-editor',
  templateUrl: './theme-token-override-editor.component.html',
  styleUrls: ['./theme-token-override-editor.component.scss'],
  imports: [
    TranslatePipe,
    DropDownListModule,
    GridModule,
    ReactiveFormsModule,
    ColorPickerFieldComponent,
    CollapseButtonComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThemeTokenOverrideEditorComponent {
  public readonly dropdownFields = { text: 'label', value: 'value' };
  public readonly tenantOverrideVariableOptions = [...tenantThemeOverrideVariables];
  public tenantOverrideModeOptions: ShowcaseOption[] = [];
  public themeTokenOverrideGridCommands: CommandModel[] = GridConstantsFactory.createStandardGridCommands();
  public isOverrideListExpanded = true;

  private rowsValue: ThemeTokenOverrideRow[] = [];

  @Input()
  public set rows(value: ThemeTokenOverrideRow[] | null) {
    this.rowsValue = value ?? [];
  }

  public get rows(): ThemeTokenOverrideRow[] {
    return this.rowsValue;
  }

  @Output() public rowsChange = new EventEmitter<ThemeTokenOverrideRow[]>();

  public overrideForm = this.formBuilder.group<ThemeTokenOverrideEditorForm>({
    overrideVariable: this.formBuilder.control<string | null>(null),
    overrideMode: this.formBuilder.control<TenantThemeOverrideMode | null>('light'),
    overrideValue: this.formBuilder.control<string | null>('')
  });

  constructor(
    private readonly translate: TranslateService,
    private readonly themeService: ThemeService,
    private readonly formBuilder: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.buildTenantOverrideModeOptions();
    this.applyCurrentModeToOverride();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildTenantOverrideModeOptions());

    this.themeService.mode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyCurrentModeToOverride();
        this.changeDetector.markForCheck();
      });

    this.overrideForm.controls.overrideVariable.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyCurrentModeToOverride());

    this.overrideForm.controls.overrideMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncOverrideValueWithSelection());

    this.overrideForm.controls.overrideValue.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyDraftPreview());
  }

  public get overrideAddOrUpdateButtonLabelKey(): string {
    const variable = String(this.overrideForm.controls.overrideVariable.value ?? '').trim();
    const mode = this.overrideForm.controls.overrideMode.value ?? this.themeService.getCurrentMode();
    return resolveThemeTokenOverrideAction(this.rows, variable, mode).action === 'update'
      ? 'i18n.domain.theme-showcase.tenantTheme.override.updateButton'
      : 'i18n.domain.theme-showcase.tenantTheme.override.addButton';
  }

  public getOverrideModeTranslationKey(mode: TenantThemeOverrideMode): string {
    if (mode === 'light') {
      return 'i18n.domain.theme-showcase.theme.light';
    }

    return 'i18n.domain.theme-showcase.theme.dark';
  }

  public onOverrideValueChanged(value: string): void {
    this.overrideForm.controls.overrideValue.setValue(String(value ?? ''), { emitEvent: true });
  }

  public toggleOverrideList(): void {
    this.isOverrideListExpanded = !this.isOverrideListExpanded;
  }

  /**
   * Clears the current token/mode/value selection (and its live preview), e.g. when the
   * surrounding tenant theme is reset to its original state.
   */
  public resetSelection(): void {
    this.overrideForm.controls.overrideVariable.setValue(null, { emitEvent: true });
  }

  public addThemeTokenOverride(): void {
    const variable = String(this.overrideForm.controls.overrideVariable.value ?? '').trim();
    const mode = this.overrideForm.controls.overrideMode.value ?? this.themeService.getCurrentMode();
    const value = String(this.overrideForm.controls.overrideValue.value ?? '').trim();

    if (!variable || !value) {
      return;
    }

    const resolution = resolveThemeTokenOverrideAction(this.rows, variable, mode);
    let nextRows: ThemeTokenOverrideRow[];
    if (resolution.action === 'update' && resolution.targetRow) {
      const targetRow = resolution.targetRow;
      nextRows = this.rows.map((row) => (row !== targetRow ? row : { ...row, mode, value }));
    } else {
      nextRows = [...this.rows, { variable, mode, value }];
    }

    this.emitRows(sortThemeTokenOverrideRows(nextRows));
  }

  public removeThemeTokenOverride(variable: string, mode: TenantThemeOverrideMode): void {
    this.emitRows(this.rows.filter((row) => !(row.variable === variable && row.mode === mode)));
  }

  public onThemeTokenOverrideCommandClick(args: CommandClickEventArgs): void {
    const rowData = args.rowData as ThemeTokenOverrideRow | undefined;
    if (!rowData) {
      return;
    }

    if (args.commandColumn?.type === SfCommandButtonType.Edit) {
      this.overrideForm.patchValue(
        {
          overrideVariable: rowData.variable,
          overrideMode: rowData.mode,
          overrideValue: rowData.value
        },
        { emitEvent: false }
      );
      return;
    }

    if (args.commandColumn?.type === SfCommandButtonType.Delete) {
      this.removeThemeTokenOverride(rowData.variable, rowData.mode);
    }
  }

  private emitRows(rows: ThemeTokenOverrideRow[]): void {
    this.rowsValue = rows;
    this.rowsChange.emit(rows);
  }

  private buildTenantOverrideModeOptions(): void {
    this.tenantOverrideModeOptions = tenantThemeOverrideModes.map((mode) => ({
      label: this.translate.instant(this.getOverrideModeTranslationKey(mode)),
      value: mode
    }));
  }

  private applyCurrentModeToOverride(): void {
    this.overrideForm.controls.overrideMode.setValue(this.themeService.getCurrentMode(), { emitEvent: false });
    this.syncOverrideValueWithSelection();
  }

  private syncOverrideValueWithSelection(): void {
    const variable = String(this.overrideForm.controls.overrideVariable.value ?? '').trim();
    const mode = this.overrideForm.controls.overrideMode.value ?? this.themeService.getCurrentMode();
    const value = resolveThemeTokenOverrideValueForSelection(this.rows, variable, mode);
    this.overrideForm.controls.overrideValue.setValue(value, { emitEvent: false });

    // Selecting a variable/mode never carries over a draft preview from a previously selected
    // token: rebuild the preview from the committed rows plus (if any) this token's own
    // committed value, discarding whatever was being test-driven for the prior selection.
    this.applyDraftPreview();
  }

  /**
   * Live-previews the value currently being edited (merged with the already committed rows)
   * so the user immediately sees the effect on the page, mirroring how the primary/secondary
   * color pickers already preview instantly without needing to click Add/Update first.
   */
  private applyDraftPreview(): void {
    const variable = String(this.overrideForm.controls.overrideVariable.value ?? '').trim();
    const mode = this.overrideForm.controls.overrideMode.value ?? this.themeService.getCurrentMode();
    const value = String(this.overrideForm.controls.overrideValue.value ?? '').trim();

    if (!variable || !value) {
      this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(this.rows));
      return;
    }

    const resolution = resolveThemeTokenOverrideAction(this.rows, variable, mode);
    let previewRows: ThemeTokenOverrideRow[];
    if (resolution.action === 'update' && resolution.targetRow) {
      const targetRow = resolution.targetRow;
      previewRows = this.rows.map((row) => (row !== targetRow ? row : { ...row, mode, value }));
    } else {
      previewRows = [...this.rows, { variable, mode, value }];
    }

    this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(previewRows));
  }
}
